import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BASE = `${API_URL}/resume`;

export const uploadMaster = async (file, token) => {
  const form = new FormData();
  form.append('file', file);
  
  const response = await axios.post(`${BASE}/master-upload`, form, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return response;
};

export const createJobApp = async (company, role, description, jobListingUrl, token) => {
  const form = new FormData();
  form.append('company', company);
  form.append('role', role);
  form.append('description', description);
  if (jobListingUrl) {
    form.append('job_listing_url', jobListingUrl);
  }

  const response = await axios.post(`${BASE}/create-application`, form, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response;
};

export const fetchApps = async (token) => {
  const response = await fetch(`${BASE}/applications`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch applications');
  }

  const data = await response.json();

  // Handle both array and object responses
  if (Array.isArray(data)) {
    return data;
  } else if (data.applications && Array.isArray(data.applications)) {
    return data.applications;
  } else {
    return [];
  }
};

export const updateStatus = async (appId, status, token) => {
  const form = new FormData();
  form.append('status', status);

  const response = await axios.patch(`${BASE}/applications/${appId}/status`, form, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response;
};

export const deleteApplication = async (appId, token) => {
  const response = await axios.delete(`${BASE}/applications/${appId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response;
};

export const downloadPDF = async (appId, token) => {
  const response = await axios.get(`${BASE}/download/${appId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    responseType: 'blob'
  });

  const url = URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = `resume_${appId}.pdf`;
  link.click();
};

export const fetchApplicationLatex = async (appId, token) => {
  const response = await fetch(`${BASE}/applications/${appId}/latex`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch LaTeX content');
  }

  return response.json();
};

export const updateApplicationFromLatex = async (appId, latexContent, token) => {
  const response = await axios.patch(`${BASE}/applications/${appId}/latex`,
    { latex_content: latexContent },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// ============================================================================
// MANUAL RESUME API FUNCTIONS
// ============================================================================

export const createManualResume = async (resumeName, token) => {
  const response = await axios.post(`${BASE}/manual`,
    { resume_name: resumeName },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

export const createManualJobApplication = async (companyName, jobRole, jobDescription, jobListingUrl, token) => {
  const response = await axios.post(`${BASE}/manual-job-application`,
    {
      company_name: companyName,
      job_role: jobRole,
      job_description: jobDescription,
      job_listing_url: jobListingUrl
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

export const fetchManualResumes = async (token) => {
  const response = await fetch(`${BASE}/manual`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch manual resumes');
  }

  const data = await response.json();
  return data.resumes || [];
};

export const fetchManualResume = async (resumeId, token) => {
  const response = await fetch(`${BASE}/manual/${resumeId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch manual resume');
  }

  return response.json();
};

export const updateManualResume = async (resumeId, resumeData, token, formatting = null) => {
  // Support both legacy format and new format with formatting options
  const payload = formatting
    ? { resume_data: resumeData, formatting }
    : resumeData;

  const response = await axios.patch(`${BASE}/manual/${resumeId}`,
    payload,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

export const deleteManualResume = async (resumeId, token) => {
  const response = await axios.delete(`${BASE}/manual/${resumeId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response;
};

export const fetchOriginalPDF = async (resumeId, token) => {
  const response = await axios.get(`${BASE}/manual/${resumeId}/original-pdf`, {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    responseType: 'blob'
  });

  return response.data;
};

export const fetchManualResumeLatex = async (resumeId, token) => {
  const response = await fetch(`${BASE}/manual/${resumeId}/latex`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch LaTeX content');
  }

  return response.json();
};

export const updateManualResumeFromLatex = async (resumeId, latexContent, token) => {
  const response = await axios.patch(`${BASE}/manual/${resumeId}/latex`,
    { latex_content: latexContent },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response;
};

// ============================================================================
// ROLE RESUME API FUNCTIONS (3-tier hierarchy: Master → Role → Job)
// ============================================================================

export const fetchRoleResumes = async (token) => {
  const response = await fetch(`${BASE}/roles`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch role resumes');
  }

  const data = await response.json();
  return data.roles || [];
};

export const fetchRoleResume = async (role, token) => {
  const response = await fetch(`${BASE}/roles/${encodeURIComponent(role)}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch role resume');
  }

  return response.json();
};

export const createRoleResume = async (role, forceRegenerate = false, token) => {
  const response = await axios.post(
    `${BASE}/roles/${encodeURIComponent(role)}?force_regenerate=${forceRegenerate}`,
    {},
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

export const updateRoleResumeOverrides = async (role, overrides, token) => {
  const response = await axios.patch(
    `${BASE}/roles/${encodeURIComponent(role)}`,
    overrides,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

export const deleteRoleResume = async (role, token) => {
  const response = await axios.delete(
    `${BASE}/roles/${encodeURIComponent(role)}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );

  return response.data;
};

export const markRoleStale = async (role, token) => {
  const response = await axios.post(
    `${BASE}/roles/${encodeURIComponent(role)}/mark-stale`,
    {},
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );

  return response.data;
};

export const fetchRoleResumeLatex = async (role, token) => {
  const response = await fetch(`${BASE}/roles/${encodeURIComponent(role)}/latex`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch LaTeX content');
  }

  return response.json();
};

export const updateRoleResumeFromLatex = async (role, latexContent, token) => {
  const response = await axios.patch(
    `${BASE}/roles/${encodeURIComponent(role)}/latex`,
    { latex_content: latexContent },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

// ============================================================================
// MASTER RESUME LATEX API FUNCTIONS
// ============================================================================

export const fetchMasterResumeLatex = async (token) => {
  const response = await fetch(`${BASE}/master/latex`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to fetch LaTeX content');
  }

  return response.json();
};

export const updateMasterResumeFromLatex = async (latexContent, token) => {
  const response = await axios.patch(
    `${BASE}/master/latex`,
    { latex_content: latexContent },
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

// ============================================================================
// Goals & Accountability API
// ============================================================================

export const getTodayProgress = async (token) => {
  const response = await axios.get(`${API_URL}/api/goals/today`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response.data;
};

export const setDailyGoal = async (dailyGoal, token) => {
  const response = await axios.post(`${API_URL}/api/goals/set`, {
    daily_goal: dailyGoal
  }, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  return response.data;
};

export const getStreakData = async (token) => {
  const response = await axios.get(`${API_URL}/api/goals/streak`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  return response.data;
};

export const getWeeklyStats = async (token) => {
  const response = await axios.get(
    `${API_URL}/api/goals/weekly-stats`,
    { headers: { 'Authorization': `Bearer ${token}` } }
  );
  return response.data;
};

// ============================================================
// Quiz API Functions
// ============================================================

const QUIZ_API_BASE = `${API_URL}/api/quiz`;

// Get available quiz filters (categories, difficulties, etc.)
export const getQuizFilters = async (token) => {
  const response = await fetch(`${QUIZ_API_BASE}/filters`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch quiz filters');
  return response.json();
};

// Study Mode
export const createStudySession = async (token, data) => {
  const response = await axios.post(`${QUIZ_API_BASE}/study/session`, data, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data;
};

export const submitStudyAnswer = async (token, data) => {
  const response = await axios.post(`${QUIZ_API_BASE}/study/answer`, data, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data;
};

export const getStudySessionResults = async (token, sessionId) => {
  const response = await fetch(`${QUIZ_API_BASE}/study/session/${sessionId}/results`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch session results');
  return response.json();
};

// Quiz Mode
export const createQuizSession = async (token, data) => {
  const response = await axios.post(`${QUIZ_API_BASE}/quiz/session`, data, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data;
};

export const submitQuiz = async (token, data) => {
  const response = await axios.post(`${QUIZ_API_BASE}/quiz/submit`, data, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data;
};

// Daily Challenge
export const getDailyChallenge = async (token, date = null) => {
  const url = date
    ? `${QUIZ_API_BASE}/daily-challenge?challenge_date=${date}`
    : `${QUIZ_API_BASE}/daily-challenge`;

  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch daily challenge');
  return response.json();
};

export const completeDailyChallenge = async (token, data) => {
  const response = await axios.post(`${QUIZ_API_BASE}/daily-challenge/complete`, data, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data;
};

export const getDailyChallengeStreak = async (token) => {
  const response = await fetch(`${QUIZ_API_BASE}/daily-challenge/streak`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch streak');
  return response.json();
};

// Progress and Analytics
export const getQuizProgress = async (token) => {
  const response = await fetch(`${QUIZ_API_BASE}/progress`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });

  // Return special object if analytics disabled (403)
  if (response.status === 403) {
    return { analytics_disabled: true };
  }

  if (!response.ok) throw new Error('Failed to fetch progress');
  return response.json();
};

export const getWeakAreas = async (token) => {
  const response = await fetch(`${QUIZ_API_BASE}/weak-areas`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch weak areas');
  return response.json();
};

// Settings
export const getQuizSettings = async (token) => {
  const response = await fetch(`${QUIZ_API_BASE}/settings`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch settings');
  return response.json();
};

export const updateQuizSettings = async (token, updates) => {
  const response = await axios.patch(`${QUIZ_API_BASE}/settings`, updates, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return response.data;
};

export const resetQuizAnalytics = async (token) => {
  const response = await axios.post(`${QUIZ_API_BASE}/settings/reset-analytics`,
    { confirm: true },
    { headers: { 'Authorization': `Bearer ${token}` } }
  );
  return response.data;
};

// Sessions
export const getUserSessions = async (token, sessionType = null, limit = 10) => {
  const params = new URLSearchParams();
  if (sessionType) params.append('session_type', sessionType);
  params.append('limit', limit.toString());

  const response = await fetch(`${QUIZ_API_BASE}/sessions?${params}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch sessions');
  return response.json();
};

export const getSessionDetails = async (token, sessionId) => {
  const response = await fetch(`${QUIZ_API_BASE}/sessions/${sessionId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('Failed to fetch session details');
  return response.json();
};

// ============================================================================
// User Locations API (for location-based job filtering)
// ============================================================================

export const getUserLocations = async (token) => {
  const response = await fetch(`${API_URL}/api/user/locations`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch locations');
  }
  return response.json();
};

export const addUserLocation = async (token, locationName, radiusMiles = 25) => {
  const response = await fetch(`${API_URL}/api/user/locations`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      location_name: locationName,
      radius_miles: radiusMiles
    })
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to add location');
  }
  return response.json();
};

export const updateUserLocation = async (token, locationId, radiusMiles) => {
  const response = await fetch(`${API_URL}/api/user/locations/${locationId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ radius_miles: radiusMiles })
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update location');
  }
  return response.json();
};

export const deleteUserLocation = async (token, locationId) => {
  const response = await fetch(`${API_URL}/api/user/locations/${locationId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete location');
  }
  return response.json();
};

export const clearAllUserLocations = async (token) => {
  const response = await fetch(`${API_URL}/api/user/locations`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to clear locations');
  }
  return response.json();
};

// ============================================================================
// XP & Gamification API
// ============================================================================

export const getXPStatus = async (token) => {
  const response = await fetch(`${API_URL}/api/xp/status`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch XP status');
  }
  return response.json();
};

export const getXPTransactions = async (token, limit = 20) => {
  const response = await fetch(`${API_URL}/api/xp/transactions?limit=${limit}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch XP transactions');
  }
  return response.json();
};